import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography, Skeleton, Select, MenuItem, FormControl, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import useCustomTheme from '../../../theme/useCustomTheme';
import '@fontsource/rubik';

const SELECT_SX = {
  fontFamily: 'Rubik',
  fontSize: '12px',
  borderRadius: '8px',
  height: 32,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E9EBE9' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#a8aea8' },
};

const MONTHS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

function generateYearOptions() {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear - 5; y <= currentYear + 1; y++) {
    years.push({ value: String(y), label: String(y) });
  }
  return years;
}

const YEAR_OPTIONS = generateYearOptions();

const CustomTooltipContent = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        border: '1px solid #E9EBE9',
        borderRadius: '8px',
        px: 1.5,
        py: 1,
        boxShadow: '0 4px 12px rgba(8,35,27,0.1)',
        fontFamily: 'Rubik',
        minWidth: 130,
        pointerEvents: 'none',
      }}
    >
      <Typography sx={{ fontFamily: 'Rubik', fontSize: 11, fontWeight: 700, color: '#6b7470', mb: 0.5 }}>
        {label}
      </Typography>
      {[...payload].reverse().map((entry) => (
        <Box key={entry.dataKey} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: entry.fill, flexShrink: 0 }} />
          <Typography sx={{ fontFamily: 'Rubik', fontSize: 12, color: '#08231B' }}>
            {entry.name}: <strong>{entry.value}</strong>
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default function MonthlyOutputChart({ monthlyOutput, projectStatus, isLoading, filter, onFilterChange }) {
  const [theme] = useCustomTheme();
  const primaryColor = theme.palette.primary.main;

  const scrollRef = useRef(null);
  const [isScrollable, setIsScrollable] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const scrollable = scrollWidth > clientWidth + 1;
    setIsScrollable(scrollable);
    setCanScrollLeft(scrollable && scrollLeft > 0);
    setCanScrollRight(scrollable && scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState);
    window.addEventListener('resize', updateScrollState);
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(el);
    Array.from(el.children).forEach((child) => resizeObserver.observe(child));
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
      resizeObserver.disconnect();
    };
  }, [updateScrollState, monthlyOutput, isLoading]);

  const scrollByAmount = useCallback((direction) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * Math.round(el.clientWidth * 0.6), behavior: 'smooth' });
  }, []);

  const allMonths = monthlyOutput?.months || [];

  const stages = projectStatus?.stages || [];
  const reviewCount     = stages.find((s) => s.stage === 'Review')?.count ?? 0;
  const completedCount  = stages.find((s) => s.stage === 'Complete')?.count ?? 0;
  const reviewPlusCompleted = reviewCount + completedCount;

  const fromMonth = filter?.fromMonth || '';
  const fromYear  = filter?.fromYear  || '';
  const toMonth   = filter?.toMonth   || '';
  const toYear    = filter?.toYear    || '';

  const handleFilterChange = useCallback((field, value) => {
    const next = { fromMonth, fromYear, toMonth, toYear, [field]: value };
    if (onFilterChange) onFilterChange(next);
  }, [fromMonth, fromYear, toMonth, toYear, onFilterChange]);

  const chartData = allMonths.map((m) => ({
    month: m.year ? `${m.month} '${String(m.year).slice(2)}` : m.month,
    Videos: m.videos,
    'Quick Turns': m.quick_turns,
  }));

  const totalDelivered = allMonths.reduce((sum, m) => sum + m.videos + m.quick_turns, 0);

  const periodLabel = (filter?.from && filter?.to)
    ? `${filter.from} – ${filter.to}`
    : 'last 6 months';

  return (
    <Box
      id="cd-monthly-chart"
      sx={{
        backgroundColor: '#ffffff',
        border: '1px solid #E9EBE9',
        borderRadius: '14px',
        boxShadow: '0 1px 2px rgba(8,35,27,0.04)',
        minWidth: 0,
      }}
    >
      {/* Header with From / To date controls */}
      <Box sx={{ px: '20px', pt: '18px', pb: '4px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography
            sx={{ fontFamily: 'Rubik', fontSize: '15px', fontWeight: 700, color: '#08231B' }}
          >
            Monthly Output
          </Typography>
          <Typography
            sx={{ fontFamily: 'Rubik', fontSize: '12px', color: '#6b7470', mt: 0.25 }}
          >
            Videos delivered (incl. 24-hr quick turns)
          </Typography>
        </Box>
        {/* From / To dropdowns */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: '2px', flexWrap: 'wrap' }}>
          <Typography sx={{ fontFamily: 'Rubik', fontSize: '12px', color: '#6b7470', fontWeight: 600 }}>
            From
          </Typography>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <Select
              value={fromMonth}
              displayEmpty
              onChange={(e) => handleFilterChange('fromMonth', e.target.value)}
              sx={SELECT_SX}
            >
              <MenuItem value="" sx={{ fontFamily: 'Rubik', fontSize: '12px' }}>Month</MenuItem>
              {MONTHS.map((m) => (
                <MenuItem key={m.value} value={m.value} sx={{ fontFamily: 'Rubik', fontSize: '12px' }}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <Select
              value={fromYear}
              displayEmpty
              onChange={(e) => handleFilterChange('fromYear', e.target.value)}
              sx={SELECT_SX}
            >
              <MenuItem value="" sx={{ fontFamily: 'Rubik', fontSize: '12px' }}>Year</MenuItem>
              {YEAR_OPTIONS.map((y) => (
                <MenuItem key={y.value} value={y.value} sx={{ fontFamily: 'Rubik', fontSize: '12px' }}>
                  {y.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography sx={{ fontFamily: 'Rubik', fontSize: '12px', color: '#6b7470', fontWeight: 600, ml: 0.5 }}>
            To
          </Typography>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <Select
              value={toMonth}
              displayEmpty
              onChange={(e) => handleFilterChange('toMonth', e.target.value)}
              sx={SELECT_SX}
            >
              <MenuItem value="" sx={{ fontFamily: 'Rubik', fontSize: '12px' }}>Month</MenuItem>
              {MONTHS.map((m) => (
                <MenuItem key={m.value} value={m.value} sx={{ fontFamily: 'Rubik', fontSize: '12px' }}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <Select
              value={toYear}
              displayEmpty
              onChange={(e) => handleFilterChange('toYear', e.target.value)}
              sx={SELECT_SX}
            >
              <MenuItem value="" sx={{ fontFamily: 'Rubik', fontSize: '12px' }}>Year</MenuItem>
              {YEAR_OPTIONS.map((y) => (
                <MenuItem key={y.value} value={y.value} sx={{ fontFamily: 'Rubik', fontSize: '12px' }}>
                  {y.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box
        sx={{
          px: '20px',
          pb: '20px',
          pt: '14px',
          display: 'flex',
          gap: 3,
          alignItems: 'stretch',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          minWidth: 0,
        }}
      >
        {/* Chart with smooth horizontal scroll for wide data */}
        <Box sx={{ position: 'relative', flex: 1, minWidth: 0 }}>
          <Box
            ref={scrollRef}
            sx={{
              minWidth: 0,
              height: { xs: 180, lg: 220, xl: 260 },
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollBehavior: 'smooth',
              '&::-webkit-scrollbar': { height: 6 },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#c4c8c4',
                borderRadius: 3,
              },
              '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            }}
          >
            {isLoading ? (
              <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: '8px' }} />
            ) : (
              <Box sx={{ minWidth: chartData.length > 8 ? chartData.length * 60 : '100%', height: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{ top: 8, right: 4, left: -20, bottom: 0 }}
                  barCategoryGap="30%"
                  barGap={3}
                >
                  <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#E9EBE9" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontFamily: 'Rubik', fontSize: 12, fill: '#6b7470' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontFamily: 'Rubik', fontSize: 11, fill: '#6b7470' }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    content={<CustomTooltipContent />}
                    cursor={{ fill: 'rgba(8,35,27,0.03)' }}
                    isAnimationActive={false}
                    wrapperStyle={{ pointerEvents: 'none' }}
                  />
                  <Bar
                    dataKey="Videos"
                    stackId="a"
                    fill={primaryColor}
                    radius={[0, 0, 0, 0]}
                    name="Videos"
                    isAnimationActive={false}
                  />
                  <Bar
                    dataKey="Quick Turns"
                    stackId="a"
                    fill="#08231B"
                    radius={[4, 4, 0, 0]}
                    name="Quick Turns"
                    isAnimationActive={false}
                  />
                </BarChart>
              </ResponsiveContainer>
              </Box>
            )}
          </Box>

          {!isLoading && isScrollable && (
            <>
              <IconButton
                size="small"
                onClick={() => scrollByAmount(-1)}
                disabled={!canScrollLeft}
                aria-label="Scroll chart left"
                sx={{
                  position: 'absolute',
                  left: 4,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: '#ffffff',
                  border: '1px solid #E9EBE9',
                  boxShadow: '0 1px 4px rgba(8,35,27,0.12)',
                  width: 26,
                  height: 26,
                  '&:hover': { backgroundColor: '#f5f6f5' },
                  '&.Mui-disabled': { opacity: 0.35 },
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => scrollByAmount(1)}
                disabled={!canScrollRight}
                aria-label="Scroll chart right"
                sx={{
                  position: 'absolute',
                  right: 4,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: '#ffffff',
                  border: '1px solid #E9EBE9',
                  boxShadow: '0 1px 4px rgba(8,35,27,0.12)',
                  width: 26,
                  height: 26,
                  '&:hover': { backgroundColor: '#f5f6f5' },
                  '&.Mui-disabled': { opacity: 0.35 },
                }}
              >
                <ChevronRightIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </>
          )}
        </Box>

        {/* Right sidebar: legend + total */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            minWidth: { xs: '100%', md: 140, lg: 155 },
            pt: 0.5,
          }}
        >
          {/* Legend */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '3px',
                  backgroundColor: primaryColor,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontFamily: 'Rubik',
                  fontSize: { xs: '11px', lg: '12px', xl: '13px' },
                  color: '#282831',
                }}
              >
                Videos
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '3px',
                  backgroundColor: '#08231B',
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontFamily: 'Rubik',
                  fontSize: { xs: '11px', lg: '12px', xl: '13px' },
                  color: '#282831',
                }}
              >
                24-hr Quick Turns
              </Typography>
            </Box>
          </Box>

          {/* Total block */}
          <Box sx={{ mt: 'auto', pt: 2 }}>
            <Typography
              sx={{
                fontFamily: 'Rubik',
                fontSize: { xs: '10px', lg: '11px' },
                fontWeight: 700,
                color: '#6b7470',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                mb: 0.25,
              }}
            >
              Total Delivered
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Rubik',
                fontSize: { xs: '28px', lg: '30px', xl: '34px' },
                fontWeight: 700,
                letterSpacing: '-0.02em',
                color: '#08231B',
                lineHeight: 1.1,
              }}
            >
              {isLoading ? <Skeleton width={60} /> : reviewPlusCompleted}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Rubik',
                fontSize: { xs: '11px', lg: '12px' },
                color: '#6b7470',
                mt: 0.25,
              }}
            >
              {periodLabel}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
